import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { taskRequest } from "./api";
import { deadlineDate, formatDeadline } from "./taskSorting";

export const userName = (user) => user.fullName || user.userName;

export default function TaskDialog({
  selection,
  lists,
  users,
  onClose,
  onChanged,
}) {
  const { t } = useTranslation();
  const [task, setTask] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [action, setAction] = useState("");
  const [loaded, setLoaded] = useState(false);
  const mutation = useRef(false);
  const fillForm = (item) =>
    setForm({
      title: item.title,
      description: item.description,
      deadline: deadlineDate(item.deadline),
      backgroundColor: item.backgroundColor || null,
      color: item.color || null,
      TaskListId: item.TaskListId,
      Assignees: item.Assignees || [],
    });

  useEffect(() => {
    let active = true;
    if (!selection.id) {
      const item = {
        title: "",
        description: "",
        TaskListId: selection.TaskListId,
        Assignees: [],
      };
      setTask(item);
      fillForm(item);
      setLoaded(true);
    } else {
      taskRequest("get_task", { taskId: selection.id })
        .then(({ task: item }) => {
          if (active) {
            setTask(item);
            fillForm(item);
            setLoaded(true);
          }
        })
        .catch((err) => {
          if (active) setError(err.message);
        });
    }
    return () => {
      active = false;
    };
  }, [selection]);

  const run = async (operation) => {
    if (mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setError("");
    try {
      await operation();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      mutation.current = false;
    }
  };
  const save = () =>
    run(async () => {
      await taskRequest("save_task", {
        taskId: task.id,
        revision: task.revision,
        task: {
          title: form.title,
          description: form.description,
          deadline: form.deadline || null,
          backgroundColor: form.backgroundColor,
          color: form.color,
          TaskListId: form.TaskListId,
          assigneeIds: form.Assignees.map((user) => user.id),
        },
      });
      onChanged();
      onClose();
    });
  const archive = () =>
    run(async () => {
      await taskRequest("save_task", {
        taskId: task.id,
        revision: task.revision,
        task: { archived: !task.archived },
      });
      onChanged();
      onClose();
    });
  const addEntry = (isComment) =>
    run(async () => {
      await taskRequest(isComment ? "add_task_comment" : "add_task_action", {
        taskId: task.id,
        text: isComment ? comment : action,
      });
      if (isComment) setComment("");
      else setAction("");
      // Keep the editor revision: a concurrent edit must still raise a conflict.
      const { task: item } = await taskRequest("get_task", { taskId: task.id });
      setTask((previous) => ({
        ...previous,
        Notes: item.Notes,
        TaskActions: item.TaskActions,
      }));
    });
  const labelValue = (key, value) => {
    if (key === "backgroundColor" || key === "color")
      return value || t("tasks.defaultColor");
    if (key === "deadline")
      return value ? formatDeadline(value) : t("tasks.noDeadline");
    if (key === "TaskListId")
      return (
        (lists.find((list) => list.id === value) || {}).name || `#${value}`
      );
    if (key === "assigneeIds")
      return (
        value
          .map((id) =>
            userName(
              users.find((user) => user.id === id) || { fullName: `#${id}` },
            ),
          )
          .join(", ") || t("tasks.unassigned")
      );
    if (key === "archived") return t(value ? "tasks.archived" : "tasks.active");
    return value || "—";
  };
  const options = users;
  const dirty =
    task &&
    form &&
    (form.title !== task.title ||
      form.description !== task.description ||
      form.deadline !== deadlineDate(task.deadline) ||
      form.TaskListId !== task.TaskListId ||
      form.backgroundColor !== (task.backgroundColor || null) ||
      form.color !== (task.color || null) ||
      JSON.stringify(form.Assignees.map((user) => user.id).sort()) !==
        JSON.stringify((task.Assignees || []).map((user) => user.id).sort()));

  return (
    <Dialog
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      maxWidth="md"
      fullWidth
      aria-labelledby="task-dialog-title"
    >
      <DialogTitle id="task-dialog-title">
        {t(selection.id ? "tasks.taskDetails" : "tasks.addCard")}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        )}
        {!loaded && !error && <CircularProgress />}
        {loaded && (
          <>
            {task.archived && (
              <Alert severity="info">{t("tasks.archivedNotice")}</Alert>
            )}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 16,
              }}
            >
              {["backgroundColor", "color"].map((key) => (
                <TextField
                  key={key}
                  type="color"
                  margin="normal"
                  id={`task-${key}`}
                  label={t(`tasks.fields.${key}`)}
                  InputLabelProps={{ shrink: true }}
                  style={{ flex: "1 1 160px" }}
                  value={
                    form[key] ||
                    (key === "backgroundColor" ? "#ffffff" : "#000000")
                  }
                  helperText={form[key] || t("tasks.defaultColor")}
                  disabled={busy || task.archived}
                  onChange={(event) =>
                    setForm({ ...form, [key]: event.target.value })
                  }
                />
              ))}
              <Button
                disabled={
                  busy ||
                  task.archived ||
                  (!form.backgroundColor && !form.color)
                }
                onClick={() =>
                  setForm({ ...form, backgroundColor: null, color: null })
                }
              >
                {t("tasks.resetColors")}
              </Button>
            </div>
            <Paper
              variant="outlined"
              style={{
                padding: 16,
                margin: "8px 0 16px",
                backgroundColor: form.backgroundColor || undefined,
                color: form.color || undefined,
              }}
            >
              <Typography variant="caption" color="inherit">
                {t("tasks.colorPreview")}
              </Typography>
              <Typography
                color="inherit"
                style={{ fontWeight: 600, overflowWrap: "anywhere" }}
              >
                {form.title || t("tasks.addCard")}
              </Typography>
            </Paper>
            <TextField
              autoFocus={!task.id}
              fullWidth
              margin="normal"
              id="task-title"
              label={t("tasks.title")}
              value={form.title}
              disabled={busy || task.archived}
              inputProps={{ maxLength: 255 }}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <TextField
              fullWidth
              margin="normal"
              multiline
              rows={4}
              variant="outlined"
              id="task-description"
              label={t("tasks.description")}
              value={form.description}
              disabled={busy || task.archived}
              inputProps={{ maxLength: 20000 }}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              type="date"
              id="task-deadline"
              label={t("tasks.deadline")}
              value={form.deadline}
              disabled={busy || task.archived}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: "1000-01-01", max: "9999-12-31" }}
              helperText={t("tasks.deadlineOptional")}
              onChange={(event) =>
                setForm({ ...form, deadline: event.target.value })
              }
            />

            <TextField
              fullWidth
              select
              margin="normal"
              id="task-list"
              label={t("tasks.list")}
              value={form.TaskListId}
              disabled={busy || task.archived}
              onChange={(e) => setForm({ ...form, TaskListId: e.target.value })}
            >
              {lists.map((list) => (
                <MenuItem key={list.id} value={list.id}>
                  {list.name}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              id="task-assignees"
              multiple
              options={options}
              getOptionLabel={userName}
              getOptionSelected={(a, b) => a.id === b.id}
              value={form.Assignees}
              disabled={busy || task.archived}
              onChange={(event, value) =>
                setForm({ ...form, Assignees: value })
              }
              renderTags={(value, getTagProps) =>
                value.map((user, index) => (
                  <Chip label={userName(user)} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="outlined"
                  label={t("tasks.assignees")}
                />
              )}
            />
            {task.id && (
              <>
                <Divider style={{ margin: "20px 0" }} />
                <Typography variant="h6">{t("tasks.comments")}</Typography>
                {!task.archived && (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      margin="normal"
                      variant="outlined"
                      id="task-comment"
                      label={t("tasks.writeComment")}
                      value={comment}
                      disabled={busy}
                      inputProps={{ maxLength: 20000 }}
                      onChange={(e) => setComment(e.target.value)}
                    />
                    <Button
                      disabled={busy || !comment.trim()}
                      onClick={() => addEntry(true)}
                      color="primary"
                    >
                      {t("tasks.addComment")}
                    </Button>
                  </>
                )}
                {!(task.Notes || []).length && (
                  <Typography color="textSecondary">
                    {t("tasks.noComments")}
                  </Typography>
                )}
                {(task.Notes || []).map((item) => (
                  <div key={item.id} style={{ margin: "16px 0" }}>
                    <Typography variant="caption" color="textSecondary">
                      {item.User ? userName(item.User) : t("tasks.unknownUser")}{" "}
                      · {new Date(item.createdAt).toLocaleString()}
                    </Typography>
                    <Typography
                      style={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {item.text}
                    </Typography>
                  </div>
                ))}
                <Divider style={{ margin: "20px 0" }} />
                <Typography variant="h6">{t("tasks.activity")}</Typography>
                {!task.archived && (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      margin="normal"
                      variant="outlined"
                      id="task-action"
                      label={t("tasks.actionTaken")}
                      value={action}
                      disabled={busy}
                      inputProps={{ maxLength: 20000 }}
                      onChange={(e) => setAction(e.target.value)}
                    />
                    <Button
                      disabled={busy || !action.trim()}
                      onClick={() => addEntry(false)}
                      color="primary"
                    >
                      {t("tasks.addAction")}
                    </Button>
                  </>
                )}
                {(task.TaskActions || []).map((item) => (
                  <div
                    key={item.id}
                    style={{ margin: "16px 0", overflowWrap: "anywhere" }}
                  >
                    <Typography variant="body2">
                      {item.User ? userName(item.User) : t("tasks.unknownUser")}{" "}
                      · {t(`tasks.actionTypes.${item.type}`)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(item.createdAt).toLocaleString()}
                    </Typography>
                    {item.text && (
                      <Typography style={{ whiteSpace: "pre-wrap" }}>
                        {item.text}
                      </Typography>
                    )}
                    {Object.entries(item.changes || {})
                      .filter(
                        ([key]) => key !== "commentId" && key !== "noteId",
                      )
                      .map(([key, change]) => (
                        <Typography
                          key={key}
                          variant="body2"
                          color="textSecondary"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {t(`tasks.fields.${key}`)}:{" "}
                          {labelValue(key, change.from)} →{" "}
                          {labelValue(key, change.to)}
                        </Typography>
                      ))}
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions style={{ flexWrap: "wrap" }}>
        {busy && <CircularProgress size={20} />}
        {task && task.id && (
          <Button disabled={busy || dirty} onClick={archive}>
            {t(task.archived ? "tasks.restore" : "tasks.archive")}
          </Button>
        )}
        {dirty && task.id && (
          <Typography variant="caption">
            {t("tasks.saveBeforeArchive")}
          </Typography>
        )}
        <Button disabled={busy} onClick={onClose}>
          {t("tasks.close")}
        </Button>
        {loaded && !task.archived && (
          <Button
            disabled={busy || !form.title.trim() || !form.TaskListId}
            onClick={save}
            color="primary"
            variant="contained"
          >
            {t("tasks.save")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
